import React, { useState, useEffect, useCallback } from 'react';
import { getNotificationLayout } from '../utils/notificationGrouping';
import { CheckIcon, AlertCircleIcon, AlertTriangleIcon, InfoIcon, XIcon } from './icons/LucideIcons';

const Notification = ({ message, type = 'info', duration = 5000, onClose }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isAnimating, setIsAnimating] = useState(false);

  const handleClose = useCallback(() => {
    setIsAnimating(false);
    setTimeout(() => {
      setIsVisible(false);
      onClose?.();
    }, 300);
  }, [onClose]);

  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        handleClose();
      }, duration);
      
      return () => clearTimeout(timer);
    }
  }, [duration, handleClose]);

  useEffect(() => {
    // Trigger entrance animation
    const timer = setTimeout(() => setIsAnimating(true), 10);
    return () => clearTimeout(timer);
  }, []);

  if (!isVisible) return null;

  // Forest toast: dark ink surface (gray-900 and surface-alt swap in dark
  // scheme, so it stays high-contrast both ways). The check is floral-300;
  // error and warning keep their meaning with the solid status dot hues.
  const animationStyles = isAnimating ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0';
  const toastStyles = `flex items-center gap-3 px-3.5 py-3 rounded-md shadow-lg bg-gray-900 text-surface-alt text-[13px] font-medium transition-all duration-300 transform ${animationStyles}`;

  const getIcon = () => {
    const iconClass = 'flex-shrink-0';
    switch (type) {
      case 'success':
        return <CheckIcon size={16} className={`${iconClass} text-floral-300`} />;
      case 'error':
        return <AlertCircleIcon size={16} className={`${iconClass} text-dot-red`} />;
      case 'warning':
        return <AlertTriangleIcon size={16} className={`${iconClass} text-dot-yellow`} />;
      default:
        return <InfoIcon size={16} className={`${iconClass} text-floral-300`} />;
    }
  };

  return (
    <div className={toastStyles}>
      {getIcon()}
      <p className="flex-1 min-w-0">{message}</p>
      <button
        type="button"
        onClick={handleClose}
        aria-label="Dismiss"
        className="flex-shrink-0 text-gray-400 hover:text-surface-alt transition-colors duration-[120ms]"
      >
        <XIcon size={16} />
      </button>
    </div>
  );
};

const VISIBLE_LIMIT = 3;

const POSITION_CLASSES = {
  default: 'top-20 right-4',
  // Shift left of the ~400px right-edge route panel on dispatch, lg+ only.
  dispatch: 'top-20 right-4 lg:right-[26rem]',
};

const NotificationContainer = ({ notifications, onRemove, position = 'default' }) => {
  const [expanded, setExpanded] = useState(false);

  // When a burst drains back under the limit, return to the collapsed default
  // so the next burst starts grouped.
  useEffect(() => {
    if (notifications.length <= VISIBLE_LIMIT && expanded) {
      setExpanded(false);
    }
  }, [notifications.length, expanded]);

  const layout = getNotificationLayout(notifications, { limit: VISIBLE_LIMIT, expanded });
  const positionClass = POSITION_CLASSES[position] || POSITION_CLASSES.default;

  const renderToast = (notification) => (
    <Notification
      key={notification.id}
      message={notification.message}
      type={notification.type}
      duration={notification.duration}
      onClose={() => onRemove(notification.id)}
    />
  );

  return (
    <div className={`fixed ${positionClass} w-96`} style={{ zIndex: 9999 }}>
      <div className="space-y-2">{layout.topItems.map(renderToast)}</div>

      {layout.mode === 'collapsed' && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="relative mt-2 w-full rounded-md bg-gray-900 px-3.5 py-2 text-[13px] font-semibold text-floral-300 shadow-lg transition-colors duration-[120ms] hover:bg-gray-800 before:absolute before:inset-x-2 before:-top-1 before:h-2 before:rounded-t-md before:bg-gray-700 before:-z-10"
        >
          +{layout.extraCount} more
        </button>
      )}

      {/* Overflow toasts stay mounted so their auto-dismiss timers keep running;
          hidden while collapsed, revealed (as part of topItems) when expanded. */}
      {layout.mode === 'collapsed' && (
        <div className="hidden">{layout.hiddenItems.map(renderToast)}</div>
      )}

      {layout.mode === 'expanded' && (
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className="mt-2 w-full rounded-md bg-gray-900 px-3.5 py-2 text-[13px] font-semibold text-floral-300 shadow-lg transition-colors duration-[120ms] hover:bg-gray-800"
        >
          Show less
        </button>
      )}
    </div>
  );
};

export { Notification, NotificationContainer };
export default Notification;